import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  deleteUser,
  updatePassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  onAuthStateChanged,
  signInWithCustomToken
} from "firebase/auth";
import { getDoc, deleteDoc,updateDoc,collection, getDocs,doc, setDoc, Timestamp, where, query, addDoc, serverTimestamp, onSnapshot, getCountFromServer  } from "firebase/firestore";
import { auth, db } from "../../src/firebase";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { AppDispatch, RootState } from "./store";



export interface UserData {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string[];
  phoneNumber?: string;
  profilePicture?: string;
  joinDate: string;
  emailAddress?: string;
  username?: string;
  userBio?: string;
  userAge?: number;
  verifyEmail?: boolean;
  verifyIdentity?: boolean;
  verifyPhone?: boolean;
   isAdmin?: boolean;   // ✅ added
  isSupport?: boolean; // ✅ added
   termsAccepted?: boolean;
address?: {
  streetAddress?: string;
    postalAddress?: string; 
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zipCode?: string;
  apartmentNumber?: string;
};

shippingAddress?: {
  streetAddress?: string;
  apartmentNumber?: string;
  postalAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zipCode?: string;
};

  [key: string]: any;
}

interface UserAddress {
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  apartmentNumber?: string;
 postalAddress?: string;
}

export interface ShippingAddress {
  streetAddress?: string;
  apartmentNumber?: string;
  postalAddress?: string;  // <-- ADD THIS
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zipCode?: string;
}


interface AuthState {
  user: UserData | null;
  allUsers: UserData[]; // ✅ Add this
  supportId: string | null;
 totalUsers: number;
  status: "idle" | "loading" | "failed";
  error: string | null;
  authLoaded: boolean;
}

const initialState: AuthState = {
  user: null,
  allUsers: [], // ✅ initialize

  status: "idle",
  error: null,
  authLoaded: false,
  supportId: null,
  totalUsers: 0
};

interface ReferralPayload {
  username: string;
  email: string;
  referralCode: string;
  referredBy: string; // userId of the inviter
  isReferred: boolean;
  createdAt?: string;
}

// ✅ Google login (strict provider consistency)
export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const uid = user.uid;
      const normalizedEmail = user.email?.trim().toLowerCase();

      if (!normalizedEmail) {
        throw new Error("Google account is missing an email address.");
      }

      // 🔍 Check if another user already uses this email (Firestore search)
      const usersRef = collection(db, "users");
      const emailQuery = query(usersRef, where("email", "==", normalizedEmail));
      const emailSnap = await getDocs(emailQuery);

      // If email already exists in Firestore, verify provider consistency
      if (!emailSnap.empty) {
        const existing = emailSnap.docs[0].data() as UserData;

        if (existing.provider === "password") {
          // 🚫 Already registered with Email/Password
          throw new Error(
            "This email is already registered with email/password. Please log in using email instead."
          );
        } else if (existing.provider !== "google") {
          // 🚫 Some other provider (GitHub, Facebook, etc.)
          throw new Error(
            "This email is already registered with another provider. Please use that to log in."
          );
        }

        // ✅ If provider is Google, proceed — return existing user data
        return existing;
      }

      // 🔍 If no existing Firestore record found → create a new user
      const [firstName = "", lastName = ""] =
        user.displayName?.split(" ") ?? [];

      const userDoc: UserData = {
        userId: uid,
        email: normalizedEmail,
        emailAddress: normalizedEmail,
        firstName,
        lastName,
        phoneNumber: user.phoneNumber ?? "",
        profilePicture: user.photoURL ?? "",
        termsAccepted: false,
        joinDate: new Date().toISOString(),
        provider: "google", // ✅ lock provider type
      };

      await setDoc(doc(db, "users", uid), userDoc);
      console.log(`✅ New Google user created: ${normalizedEmail}`);
      return userDoc;
    } catch (err: any) {
      console.error("❌ Google login error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);



// ✅ Email/Password register (with username + referral link)
export const registerWithEmail = createAsyncThunk(
  "auth/registerWithEmail",
  async (
    {
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      profilePicture,
      username,
    }: Omit<UserData, "userId" | "joinDate"> & { password: string; username: string },
    { rejectWithValue }
  ) => {
    try {
      // ✅ Normalize email (important to avoid case sensitivity issues)
      const normalizedEmail = email.trim().toLowerCase();

      // ✅ Reference to Firestore users collection
      const usersRef = collection(db, "users");

      // ✅ Check if email already exists under either "email" or "emailAddress"
      const emailQuery = query(
        usersRef,
        where("email", "in", [normalizedEmail, email]),
      );
      const emailSnap = await getDocs(emailQuery);

      // 🔎 Fallback query (if some older docs only have emailAddress field)
      let existingUser: UserData | null = null;
      if (!emailSnap.empty) {
        existingUser = emailSnap.docs[0].data() as UserData;
      } else {
        const emailAddrQuery = query(usersRef, where("emailAddress", "==", normalizedEmail));
        const emailAddrSnap = await getDocs(emailAddrQuery);
        if (!emailAddrSnap.empty) {
          existingUser = emailAddrSnap.docs[0].data() as UserData;
        }
      }

      // ✅ If we found a user with this email, block duplicate registration
      if (existingUser) {
        if (existingUser.provider === "google") {
          throw new Error(
            "This email is already registered with Google. Please log in using Google."
          );
        } else if (existingUser.provider === "password") {
          throw new Error("This email is already registered.");
        } else {
          throw new Error("This email is already linked to another provider.");
        }
      }

      // ✅ Check if username is taken
      const usernameQuery = query(usersRef, where("username", "==", username.trim()));
      const usernameSnap = await getDocs(usernameQuery);
      if (!usernameSnap.empty) {
        throw new Error("This username is already taken. Please choose another.");
      }

      // ✅ Create Firebase Auth user
      const userCred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const uid = userCred.user.uid;

      // ✅ Generate referral link
      const referralLink = `https://areluxe.com/referral/?referral-code=${encodeURIComponent(
        username
      )}`;

      // ✅ Firestore user document
      const userDoc: UserData = {
        userId: uid,
        email: normalizedEmail,
        emailAddress: normalizedEmail,
        firstName,
        lastName,
        phoneNumber,
        profilePicture,
        username,
        referralLink,
        termsAccepted: false,
        joinDate: new Date().toISOString(),
        provider: "password", // 🔐 lock provider type
      };

      await setDoc(doc(db, "users", uid), userDoc);

      console.log(`✅ User registered with referral link: ${referralLink}`);
      return userDoc;
    } catch (err: any) {
      console.error("❌ Registration error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);


export const loginWithEmail = createAsyncThunk(
  "auth/loginWithEmail",
  async ({ email, password }: { email: string; password: string }) => {
    const userCred = await signInWithEmailAndPassword(auth, email, password);
    const snapshot = await getDoc(doc(db, "users", userCred.user.uid));
    return snapshot.data() as UserData;
  }
);




export const signInWithCustomTokenThunk = createAsyncThunk(
  'auth/signInWithCustomToken',
  async (token: string, { rejectWithValue }) => {
    try {
      const result = await signInWithCustomToken(auth, token);
      const uid = result.user.uid;

      const docSnap = await getDoc(doc(db, 'users', uid));
      if (!docSnap.exists()) throw new Error('User not found in Firestore');

      return docSnap.data() as UserData;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);





export const logout = createAsyncThunk("auth/logout", async () => {
  await signOut(auth);
});

export const deleteAccount = createAsyncThunk(
  "auth/deleteAccount",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user");

      const uid = currentUser.uid;

      const userRef = doc(db, "users", uid);
      const snap = await getDoc(userRef);

      const storage = getStorage();

      if (snap.exists()) {
        const data = snap.data();

        if (data?.profilePicture) {
          try {
            const imageRef = ref(storage, data.profilePicture);
            await deleteObject(imageRef);
          } catch (err) {
            console.warn("⚠️ Failed to delete profile picture", err);
          }
        }

        try {
          const defaultRef = ref(storage, `profilePictures/${uid}.jpg`);
          await deleteObject(defaultRef);
        } catch (err) {}

        await deleteDoc(userRef);
      }

      await currentUser.delete();

      return uid;

    } catch (err: any) {
      console.error("🔥 deleteAccount error:", err);
      return rejectWithValue(err.message);
    }
  }
);


export const deactivateAccount = createAsyncThunk(
  "auth/deactivateAccount",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");

      const userRef = doc(db, "users", currentUser.uid);

      await updateDoc(userRef, {
        accountStatus: "deactivated",
        deactivatedAt: serverTimestamp(),
      });

      return currentUser.uid;

    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const reactivateAccount = createAsyncThunk(
  "auth/reactivateAccount",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");

      const userRef = doc(db, "users", currentUser.uid);

      await updateDoc(userRef, {
        accountStatus: "active",
        reactivatedAt: serverTimestamp(),
      });

      return currentUser.uid;

    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);


export const fetchUserData = createAsyncThunk(
  "auth/fetchUserData",
  async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in");

    const userRef = doc(db, "users", currentUser.uid);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) throw new Error("User document not found");

    const data = docSnap.data();

    // Convert Firestore Timestamp safely
    const reviewedAt =
      data?.reviewedAt instanceof Timestamp
        ? data.reviewedAt.toDate().toISOString()
        : data?.reviewedAt ?? null;

    // ----------- ADDRESS FIX -----------
    // Ensure `address {}` exists even if missing, and normalize fields.
 const address = {
  streetAddress: data?.address?.streetAddress || "",
  postalAddress: data?.address?.postalAddress || "",
  city: data?.address?.city || "",
  state: data?.address?.state || "",
  country: data?.address?.country || "",
  countryCode: data?.address?.countryCode || "",
  zipCode: data?.address?.zipCode || "",
  apartmentNumber: data?.address?.apartmentNumber || "",
};

const shippingAddress = {
  streetAddress: data?.shippingAddress?.streetAddress || "",
  postalAddress: data?.shippingAddress?.postalAddress || "",
  city: data?.shippingAddress?.city || "",
  state: data?.shippingAddress?.state || "",
  country: data?.shippingAddress?.country || "",
  countryCode: data?.shippingAddress?.countryCode || "",
  zipCode: data?.shippingAddress?.zipCode || "",
  apartmentNumber: data?.shippingAddress?.apartmentNumber || "",
};

 return {
  ...(data as UserData),
  reviewedAt,
  address,
  shippingAddress,
};
  }
);


export const sendResetEmail = createAsyncThunk("auth/sendResetEmail", async (email: string) => {
  await sendPasswordResetEmail(auth, email);
});





// Update user profile
export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (updatedData: Partial<UserData>) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in");

    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, updatedData, { merge: true });

    const updatedDoc = await getDoc(userRef);
    return updatedDoc.data() as UserData;
  }
);


export const uploadProfilePicture = createAsyncThunk(
  "auth/uploadProfilePicture",
  async ({ file }: { file: File }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in");

    const storage = getStorage();
    const filePath = `profilePictures/${currentUser.uid}/${file.name}`;
    const storageRef = ref(storage, filePath);

    // 🔹 Upload the image
    await uploadBytes(storageRef, file);

    // 🔹 Get downloadable URL
    const downloadURL = await getDownloadURL(storageRef);

    // 🔹 Save to Firestore
    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, { profilePicture: downloadURL }, { merge: true });

    const updatedDoc = await getDoc(userRef);
    return updatedDoc.data() as UserData;
  }
);




export const uploadLivenessVideo = createAsyncThunk(
  "auth/uploadLivenessVideo",
  async (file: File, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not logged in");

      const storage = getStorage();
      const filePath = `identityVideo/${currentUser.uid}/liveness-${Date.now()}.webm`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const videoUrl = await getDownloadURL(storageRef);

      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, { videoUrl, verifyVideo: true }, { merge: true });

      const updatedDoc = await getDoc(userRef);
      return updatedDoc.data() as UserData;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);


export const uploadIdentityDocument = createAsyncThunk(
  "auth/uploadIdentityDocument",
  async ({ file, side }: { file: File; side: "front" | "back" }, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not logged in");

      const storage = getStorage();
      const filePath = `identityDocs/${currentUser.uid}/id-${side}-${Date.now()}.jpg`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const update: Partial<UserData> = {
        verifyDocument: true,
      };

      if (side === "front") update.idFrontUrl = downloadURL;
      if (side === "back") update.idBackUrl = downloadURL;

      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, update, { merge: true });

      const updatedDoc = await getDoc(userRef);
      return updatedDoc.data() as UserData;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);




// src/redux/authSlice.ts// Updated sendEmailVerificationCode thunk with fake email in transporter config
export const sendEmailVerificationCode = createAsyncThunk(
  "auth/sendEmailVerificationCode",
  async ({ email }: { email: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user = state.auth.user;

      if (!user?.userId) throw new Error("Missing user ID");
      if (!email) throw new Error("Missing email");
      const response = await fetch(process.env.REACT_APP_EMAIL_VERIFICATION_API!, {

      
      

      

      /*
       works-     const response = await fetch("https://areluxe.com/sendVerification", {

      const response = await fetch(`${process.env.REACT_APP_EMAIL_VERIFICATION_API}`, {
      const response = await fetch("http://localhost:5000/api/send-verification", { 
      
      */
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.userId, email }),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      return true;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const confirmEmailVerificationCode = createAsyncThunk(
  "auth/confirmEmailVerificationCode",
  async ({ inputCode }: { inputCode: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user = state.auth.user;
      if (!user) throw new Error("User not found");

      const userRef = doc(db, "users", user.userId);
      const snapshot = await getDoc(userRef);
      const data = snapshot.data();

      if (snapshot.exists() && data?.verificationCode === inputCode.trim()) {
        await updateDoc(userRef, {
          verifyEmail: true,
          verificationCode: "", // Optional: Clear the code
        });

        dispatch(updateUserProfile({ verifyEmail: true }));
        return true;
      } else {
        throw new Error("Verification code is incorrect");
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);



///////////////Verify PHONE/////////////////




export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    const user = auth.currentUser;
    if (!user?.email) throw new Error("User not logged in or missing email");

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }
);



export const listenForAuthChanges = createAsyncThunk(
  "auth/listenForAuthChanges",
  async (_, { dispatch }) => {
    return new Promise<UserData | null>((resolve) => {
      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          const snapshot = await getDoc(doc(db, "users", firebaseUser.uid));
          const userData = snapshot.data() as UserData;

          // ✅ Patch userId if missing
          if (!userData.userId) {
            userData.userId = firebaseUser.uid;
          }

          resolve(userData);
        } else {
          resolve(null);
        }
      });
    });
  }
);



export const fetchAllUsers = createAsyncThunk<UserData[], void, { rejectValue: string }>(
  "auth/fetchAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));

      const users: UserData[] = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        // ⛑️ Fix only reviewedAt field safely
        const reviewedAt =
          data.reviewedAt instanceof Timestamp
            ? data.reviewedAt.toDate().toISOString()
            : data.reviewedAt ?? null;

        return {
          ...(data as UserData),
          userId: docSnap.id,
          reviewedAt, // overrides any Timestamp
        };
      });

      return users;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);


export const getSupportId = createAsyncThunk<UserData | null, void, { rejectValue: string }>(
  "auth/getSupportId",
  async (_, { rejectWithValue }) => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const supportUser = querySnapshot.docs
        .map((doc) => {
          const data = doc.data() as UserData;
          return { ...data, userId: doc.id };
        })
        .find((user) => user.role?.includes("support")
      )
      return supportUser || null;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Generate and save referral link for the current user
export const generateReferralLink = createAsyncThunk(
  "auth/generateReferralLink",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user = state.auth.user;

      if (!user || !user.username) {
        throw new Error("Username missing — cannot create referral link.");
      }

      const referralLink = `https://areluxe.com/referral/?referral-code=${encodeURIComponent(user.username)}`;

      // Save it to Firestore
      const userRef = doc(db, "users", user.userId);
      await updateDoc(userRef, { referralLink });

      return { ...user, referralLink };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);





export const listenToReferrerId = createAsyncThunk(
  "auth/listenToReferrerId",
  async (
    {
      referralCode,
      username,
      email,
      onUpdate,
    }: {
      referralCode: string;
      username?: string;
      email?: string;
      onUpdate: (data: {
        referrerId: string | null;
        userExists: boolean;
      }) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!referralCode) throw new Error("Missing referral code");

      // 🔹 Listen for referrer changes
      const referrerQ = query(
        collection(db, "users"),
        where("username", "==", referralCode.trim())
      );

      const unsubscribeReferrer = onSnapshot(referrerQ, async (snap) => {
        let referrerId: string | null = null;
        let userExists = false;

        if (!snap.empty) {
          referrerId = snap.docs[0].id;
        }

        // 🔹 Realtime check for email or username duplicates
        if (username || email) {
          const usersRef = collection(db, "users");

          // Username listener
          const usernameQ = query(usersRef, where("username", "==", username || ""));
          const emailQ = query(usersRef, where("emailAddress", "==", email || ""));

          // Listen to username
          const unsubscribeUsername = onSnapshot(usernameQ, (snap2) => {
            userExists = !snap2.empty;
            onUpdate({ referrerId, userExists });
          });

          // Listen to email
          const unsubscribeEmail = onSnapshot(emailQ, (snap3) => {
            userExists = userExists || !snap3.empty;
            onUpdate({ referrerId, userExists });
          });

          // Return combined unsubscribes
          return () => {
            unsubscribeUsername();
            unsubscribeEmail();
          };
        } else {
          onUpdate({ referrerId, userExists });
        }
      });

      return () => unsubscribeReferrer();
    } catch (error: any) {
      console.error("❌ Error listening to referrer:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const upsertRefferrals = createAsyncThunk(
  "auth/upsertRefferrals",
  async (data: ReferralPayload, { rejectWithValue }) => {
    try {
      const referralRef = collection(db, "referrals");
      const usersRef = collection(db, "users");

      // Prevent duplicate referral entries by username
      const q = query(referralRef, where("username", "==", data.username));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        // Update existing referral record
        const existingDoc = snapshot.docs[0];
        await updateDoc(existingDoc.ref, {
          ...data,
          isRegistered: false,
          hasListings: false,
          hasBookings: false,
          updatedAt: serverTimestamp(),
        });
        console.log("Referral updated:", data.username);
      } else {
        // Add new referral record
        await addDoc(referralRef, {
          ...data,
          isRegistered: false,
          hasListings: false,
          hasBookings: false,
          createdAt: data.createdAt || serverTimestamp(),
        });
        console.log("Referral created:", data.username);
      }

      // Optionally also add/update referred user record in 'users'
      // (if you want their onboarding flags immediately visible)
      const userQuery = query(usersRef, where("emailAddress", "==", data.email));
      const userSnap = await getDocs(userQuery);

      if (!userSnap.empty) {
        // Update existing user record with referral onboarding fields
        const userDoc = userSnap.docs[0];
        await updateDoc(userDoc.ref, {
          isRegistered: false,
          hasListings: false,
          hasBookings: false,
          isReferred: true,
          referredBy: data.referredBy,
          referralCode: data.referralCode,
          updatedAt: serverTimestamp(),
        });
      }

      return {
        ...data,
        isRegistered: false,
        hasListings: false,
        hasBookings: false,
      };
    } catch (error: any) {
      console.error("Error saving referral:", error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * ✅ Realtime listener for referred user's referral data
 * Listens to changes in the 'referrals' collection by username or email.
 */
export const listenToReferredUserData = createAsyncThunk(
  "auth/listenToReferredUserData",
  async (
    {
      username,
      email,
      onUpdate,
    }: {
      username?: string;
      email?: string;
      onUpdate: (data: {
        referralId: string | null;
        referralData: any | null;
      }) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!username && !email)
        throw new Error("Missing username or email to listen for referrals.");

      const referralsRef = collection(db, "referrals");

      // 🔹 Build query (username preferred, fallback to email)
      const q = username
        ? query(referralsRef, where("username", "==", username))
        : query(referralsRef, where("email", "==", email));

      // 🔹 Setup realtime listener
      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          onUpdate({
            referralId: doc.id,
            referralData: doc.data(),
          });
        } else {
          onUpdate({
            referralId: null,
            referralData: null,
          });
        }
      });

      // Return unsubscribe function
      return () => unsubscribe();
    } catch (error: any) {
      console.error("❌ Error listening to referred user data:", error);
      return rejectWithValue(error.message);
    }
  }
);


/**
 * ✅ Global inactivity timeout
 * Logs out the current user after a period of inactivity.
 */
export const startInactivityWatcher = createAsyncThunk<
  void,
  { timeoutMs?: number },
  { dispatch: AppDispatch; state: RootState }
>(
  "auth/startInactivityWatcher",
  async ({ timeoutMs = 5 * 60 * 1000 }, { dispatch, getState }) => {

    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const { user } = getState().auth;
        if (user) {
          console.warn("⏳ Session expired due to inactivity");
          await dispatch(logout());
          alert("You’ve been logged out due to inactivity.");
          window.location.href = "/AuthSwitch"; // redirect safely
        }
      }, timeoutMs);
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => resetTimer();

    activityEvents.forEach((event) =>
      window.addEventListener(event, handleActivity)
    );

    resetTimer();

    // Cleanup on unmount or reload
    window.addEventListener("beforeunload", () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      clearTimeout(timer);
    });
  }
);



export const listenToTotalUsers = createAsyncThunk(
  "auth/listenToTotalUsers",
  async (_, { dispatch }) => {
    const usersRef = collection(db, "users");

    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      dispatch(setTotalUsers(snapshot.size));
    });

    return unsubscribe; // optional cleanup
  }
);

export const fetchTotalUsers = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>(
  "auth/fetchTotalUsers",
  async (_, { rejectWithValue }) => {
    try {
      const usersRef = collection(db, "users");
      const snapshot = await getCountFromServer(usersRef);

      return snapshot.data().count;
    } catch (err) {
      return rejectWithValue(
        err instanceof Error ? err.message : "Failed to fetch total users"
      );
    }
  }
);


const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthState: (state) => {
    state.user = null;
    state.status = "idle";
    state.error = null;
  },
  setTotalUsers: (state, action) => {
    state.totalUsers = action.payload;
  },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerWithEmail.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
      })
      .addCase(listenForAuthChanges.fulfilled, (state, action) => {
        state.user = action.payload;
        state.authLoaded = true;
      })
      .addCase(listenForAuthChanges.rejected, (state) => {
        state.authLoaded = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.allUsers = action.payload;
      })
      .addCase(signInWithCustomTokenThunk.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(fetchTotalUsers.fulfilled, (state, action) => {
  state.totalUsers = action.payload;
})
      .addCase(getSupportId.fulfilled, (state, action) => {
        state.supportId = action.payload?.userId || null;
      })
      .addCase(generateReferralLink.fulfilled, (state, action) => {
  state.user = action.payload;
})

.addCase(deleteAccount.fulfilled, (state) => {
  state.user = null;
})

.addCase(deactivateAccount.fulfilled, (state) => {
  if (state.user) {
    state.user.accountStatus = "deactivated";
  }
})

.addCase(reactivateAccount.fulfilled, (state) => {
  if (state.user) {
    state.user.accountStatus = "active";
  }
})
  },
});

export const { clearAuthState,setTotalUsers } = authSlice.actions;
export default authSlice.reducer;
